/**
 * Refcounted module-scoped sharing for expensive subscriptions (Firestore
 * onSnapshot). Multiple hook mounts with the same key share ONE underlying
 * subscription and one in-memory value; the subscription is opened on the
 * first mount and torn down when the last mount unsubscribes.
 *
 * Why: `useListingMatchCache` + `useListingNotifications` are mounted by two
 * sibling widgets on the dashboard main page, and each mount used to open its
 * own unbounded listener — the same collections downloaded twice per page load
 * (measured: 2× 3,747 `listing-matches` docs for one customer). A provider
 * would also solve it, but this keeps every call site untouched and carries no
 * provider-placement risk for consumers outside the dashboard page tree.
 */
export function createSharedSnapshotStore<T>(options: {
  initialValue: () => T;
  subscribe: (key: string, emit: (value: T) => void) => () => void;
}): { subscribe: (key: string, onChange: (value: T) => void) => () => void } {
  type Entry = { value: T; listeners: Set<(value: T) => void>; unsubscribe: () => void };
  const entries = new Map<string, Entry>();

  return {
    subscribe(key: string, onChange: (value: T) => void): () => void {
      let entry = entries.get(key);
      if (!entry) {
        const newEntry: Entry = {
          value: options.initialValue(),
          listeners: new Set(),
          unsubscribe: () => {}
        };
        entries.set(key, newEntry);
        newEntry.unsubscribe = options.subscribe(key, (value) => {
          newEntry.value = value;
          newEntry.listeners.forEach((listener) => listener(value));
        });
        entry = newEntry;
      }
      entry.listeners.add(onChange);
      // Late subscribers get the already-loaded value immediately instead of
      // waiting for the next snapshot.
      onChange(entry.value);
      return () => {
        entry!.listeners.delete(onChange);
        if (entry!.listeners.size === 0) {
          entry!.unsubscribe();
          entries.delete(key);
        }
      };
    }
  };
}
